import React, { useState, useEffect } from 'react';
import { 
  Ticket, 
  Plus, 
  Search, 
  Trash2, 
  Check, 
  Copy, 
  RefreshCw, 
  Tag, 
  Percent, 
  Coins, 
  Calendar, 
  CheckCircle2, 
  AlertCircle, 
  Sparkles, 
  Sliders, 
  ShieldCheck, 
  ExternalLink,
  Info,
  X,
  TrendingUp,
  Zap,
  Eye,
  Edit3,
  Clock,
  Users,
  BarChart3,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
  EscrowVoucher, 
  VoucherRedemption,
  getVouchers, 
  getVoucherRedemptions,
  saveVoucher, 
  updateVoucher,
  toggleVoucherActive, 
  deleteVoucher,
  getVoucherStatus,
  VoucherStatusType
} from '../services/voucherService';

export const VouchersView: React.FC = () => {
  const { currentUser } = useAuth();
  const [vouchers, setVouchers] = useState<EscrowVoucher[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | VoucherStatusType>('all');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Notification Toast
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Create Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [discountAmount, setDiscountAmount] = useState<number>(1000);
  const [minOrderAmount, setMinOrderAmount] = useState<number>(5000);
  const [maxUsageCount, setMaxUsageCount] = useState<number>(500);
  const [usagePerUser, setUsagePerUser] = useState<'once' | 'multiple'>('once');
  const [maxPerUser, setMaxPerUser] = useState<number>(1);
  const [startsAt, setStartsAt] = useState<string>(new Date().toISOString().split('T')[0]);
  const [expiresAt, setExpiresAt] = useState<string>('2026-12-31');
  const [description, setDescription] = useState('');

  // View Details & Usage Modal State
  const [selectedVoucherForDetails, setSelectedVoucherForDetails] = useState<EscrowVoucher | null>(null);
  const [redemptions, setRedemptions] = useState<VoucherRedemption[]>([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);

  // Edit Voucher Modal State
  const [editingVoucher, setEditingVoucher] = useState<EscrowVoucher | null>(null);
  const [editDiscountType, setEditDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [editDiscountAmount, setEditDiscountAmount] = useState<number>(1000);
  const [editMinOrderAmount, setEditMinOrderAmount] = useState<number>(5000);
  const [editMaxUsageCount, setEditMaxUsageCount] = useState<number>(500);
  const [editUsagePerUser, setEditUsagePerUser] = useState<'once' | 'multiple'>('once');
  const [editMaxPerUser, setEditMaxPerUser] = useState<number>(1);
  const [editStartsAt, setEditStartsAt] = useState<string>('');
  const [editExpiresAt, setEditExpiresAt] = useState<string>('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState<boolean>(true);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    loadVouchers();
  }, []);

  const loadVouchers = async () => {
    setLoading(true);
    try {
      const data = await getVouchers();
      setVouchers(data);
    } catch (err) {
      console.error('Failed to load vouchers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (voucherCode: string) => {
    navigator.clipboard.writeText(voucherCode);
    setCopiedCode(voucherCode);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleToggleStatus = async (voucher: EscrowVoucher) => {
    const newStatus = !voucher.isActive;
    try {
      await toggleVoucherActive(voucher.code, newStatus);
      setVouchers((prev) =>
        prev.map((v) => (v.code === voucher.code ? { ...v, isActive: newStatus } : v))
      );
      if (selectedVoucherForDetails?.code === voucher.code) {
        setSelectedVoucherForDetails((prev) => prev ? { ...prev, isActive: newStatus } : null);
      }
      showToast('success', `Voucher ${voucher.code} is now ${newStatus ? 'Active' : 'Disabled'}.`);
    } catch (err) {
      showToast('error', 'Failed to update voucher status.');
    }
  };

  const handleDelete = async (voucherCode: string) => {
    if (window.confirm(`Are you sure you want to delete voucher "${voucherCode}"? Users will no longer be able to apply it.`)) {
      try {
        await deleteVoucher(voucherCode);
        setVouchers((prev) => prev.filter((v) => v.code !== voucherCode));
        if (selectedVoucherForDetails?.code === voucherCode) {
          setSelectedVoucherForDetails(null);
        }
        showToast('success', `Voucher ${voucherCode} deleted successfully.`);
      } catch (err) {
        showToast('error', 'Failed to delete voucher.');
      }
    }
  };

  const handleGenerateCode = () => {
    const prefixes = ['SAVE', 'WELCOME', 'ECO', 'ESCROW', 'DEAL', 'REUSE'];
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    setCode(`${prefix}-${randomSuffix}`);
  };

  const handleCreateVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      showToast('error', 'Please provide a coupon code.');
      return;
    }

    setSaving(true);
    try {
      await saveVoucher(
        {
          code: code.trim().toUpperCase(),
          discountType,
          discountAmount: Number(discountAmount),
          minOrderAmount: Number(minOrderAmount),
          maxUsageCount: Number(maxUsageCount),
          usagePerUser,
          maxPerUser: usagePerUser === 'once' ? 1 : Number(maxPerUser || 0),
          startsAt: startsAt || null,
          expiresAt: expiresAt || null,
          description: description.trim() || `${discountType === 'fixed' ? '₦' + discountAmount : discountAmount + '%'} Escrow Protection Fee Credit`,
          applicableTo: 'escrow_fee',
          isActive: true,
        },
        currentUser?.email || 'superadmin@reuseit.shop'
      );

      await loadVouchers();
      setShowCreateModal(false);
      resetForm();
      showToast('success', `In-app voucher ${code.trim().toUpperCase()} created and scheduled successfully!`);
    } catch (err: any) {
      console.error(err);
      showToast('error', err.message || 'Failed to save voucher.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDetails = async (voucher: EscrowVoucher) => {
    setSelectedVoucherForDetails(voucher);
    setLoadingRedemptions(true);
    try {
      const redList = await getVoucherRedemptions(voucher.code);
      setRedemptions(redList);
    } catch (err) {
      console.warn('Error loading redemptions:', err);
    } finally {
      setLoadingRedemptions(false);
    }
  };

  const handleOpenEdit = (voucher: EscrowVoucher) => {
    setEditingVoucher(voucher);
    setEditDiscountType(voucher.discountType);
    setEditDiscountAmount(voucher.discountAmount);
    setEditMinOrderAmount(voucher.minOrderAmount);
    setEditMaxUsageCount(voucher.maxUsageCount);
    setEditUsagePerUser(voucher.usagePerUser || 'once');
    setEditMaxPerUser(voucher.maxPerUser || 1);
    setEditStartsAt(voucher.startsAt || '');
    setEditExpiresAt(voucher.expiresAt || '');
    setEditDescription(voucher.description || '');
    setEditIsActive(voucher.isActive);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVoucher) return;

    setSavingEdit(true);
    try {
      const updates: Partial<EscrowVoucher> = {
        discountType: editDiscountType,
        discountAmount: Number(editDiscountAmount),
        minOrderAmount: Number(editMinOrderAmount),
        maxUsageCount: Number(editMaxUsageCount),
        usagePerUser: editUsagePerUser,
        maxPerUser: editUsagePerUser === 'once' ? 1 : Number(editMaxPerUser || 0),
        startsAt: editStartsAt || null,
        expiresAt: editExpiresAt || null,
        description: editDescription.trim(),
        isActive: editIsActive,
      };

      await updateVoucher(editingVoucher.code, updates);

      setVouchers((prev) =>
        prev.map((v) => (v.code === editingVoucher.code ? { ...v, ...updates } : v))
      );

      if (selectedVoucherForDetails?.code === editingVoucher.code) {
        setSelectedVoucherForDetails((prev) => prev ? { ...prev, ...updates } : null);
      }

      setEditingVoucher(null);
      showToast('success', `Voucher ${editingVoucher.code} updated successfully!`);
    } catch (err: any) {
      console.error('Failed to update voucher:', err);
      showToast('error', err.message || 'Failed to update voucher.');
    } finally {
      setSavingEdit(false);
    }
  };

  const resetForm = () => {
    setCode('');
    setDiscountType('fixed');
    setDiscountAmount(1000);
    setMinOrderAmount(5000);
    setMaxUsageCount(500);
    setUsagePerUser('once');
    setMaxPerUser(1);
    setStartsAt(new Date().toISOString().split('T')[0]);
    setExpiresAt('2026-12-31');
    setDescription('');
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  // KPIs
  const totalActive = vouchers.filter((v) => v.isActive).length;
  const totalClaimed = vouchers.reduce((acc, v) => acc + (v.usedCount || 0), 0);
  const estimatedSavingsNaira = vouchers.reduce((acc, v) => {
    if (v.discountType === 'fixed') {
      return acc + (v.usedCount || 0) * v.discountAmount;
    }
    return acc + (v.usedCount || 0) * 1200; // estimated avg fee savings
  }, 0);

  const filteredVouchers = vouchers.filter((v) => {
    const matchesSearch =
      v.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.description.toLowerCase().includes(searchQuery.toLowerCase());
    const opStatus = getVoucherStatus(v).status;
    const matchesStatus =
      statusFilter === 'all'
        ? true
        : opStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {notification && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-2xl text-xs font-bold shadow-2xl flex items-center gap-2.5 transition-all ${
            notification.type === 'success'
              ? 'bg-emerald-500 text-forest-void border border-emerald-400'
              : 'bg-rose-500 text-white border border-rose-400'
          }`}
        >
          {notification.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black tracking-tight text-white dark:text-white light:text-[#0D1C17]">
              Escrow Fee Credit Vouchers & Coupons
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              In-App Checkout Engine
            </span>
          </div>
          <p className="text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mt-1">
            Create, schedule, and track in-app coupon codes granting Escrow Protection Fee credits to marketplace users at checkout.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadVouchers}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2.5 rounded-2xl text-xs font-semibold bg-[#152a23] hover:bg-[#1c382f] text-[#90cbb7] border border-[#22493c] transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Sync</span>
          </button>

          <button
            onClick={() => {
              handleGenerateCode();
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all"
          >
            <Plus className="w-4 h-4 stroke-[3]" />
            <span>Create New Voucher</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-3xl p-5 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card">
          <div className="flex items-center justify-between text-xs text-[#90cbb7]">
            <span className="font-bold uppercase tracking-wider text-[11px]">Active In-App Vouchers</span>
            <Ticket className="w-4 h-4 text-brand-400" />
          </div>
          <p className="text-2xl font-black text-white dark:text-white light:text-[#0D1C17] mt-2">
            {totalActive} Codes Live
          </p>
          <p className="text-[11px] text-[#90cbb7]/70 mt-1">
            Redeemable by buyers across Nigeria
          </p>
        </div>

        <div className="rounded-3xl p-5 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card">
          <div className="flex items-center justify-between text-xs text-[#90cbb7]">
            <span className="font-bold uppercase tracking-wider text-[11px]">Total Checkout Redemptions</span>
            <TrendingUp className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-2xl font-black text-white dark:text-white light:text-[#0D1C17] mt-2">
            {totalClaimed.toLocaleString()} Used
          </p>
          <p className="text-[11px] text-[#90cbb7]/70 mt-1">
            Escrow fee credits claimed at checkout
          </p>
        </div>

        <div className="rounded-3xl p-5 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card">
          <div className="flex items-center justify-between text-xs text-[#90cbb7]">
            <span className="font-bold uppercase tracking-wider text-[11px]">Escrow Subsidy Granted</span>
            <Coins className="w-4 h-4 text-amber-400" />
          </div>
          <p className="text-2xl font-black text-emerald-400 mt-2">
            ₦{estimatedSavingsNaira.toLocaleString()}
          </p>
          <p className="text-[11px] text-[#90cbb7]/70 mt-1">
            Direct user acquisition escrow savings
          </p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="rounded-3xl p-4 bg-[#152a23] dark:bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#D5E4DC] shadow-card flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#90cbb7]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search coupon code or description..."
            className="w-full pl-9 pr-4 py-2.5 rounded-2xl text-xs bg-[#10231d] border border-[#22493c] text-white placeholder-[#90cbb7]/60 outline-none focus:border-brand-500 transition-colors"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
          {[
            { id: 'all', label: 'All Vouchers' },
            { id: 'active', label: 'Active & Live' },
            { id: 'scheduled', label: 'Scheduled' },
            { id: 'expired', label: 'Expired' },
            { id: 'depleted', label: 'Limit Reached' },
            { id: 'disabled', label: 'Disabled' },
          ].map((st) => (
            <button
              key={st.id}
              onClick={() => setStatusFilter(st.id as any)}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                statusFilter === st.id
                  ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                  : 'bg-[#10231d] text-[#90cbb7] hover:text-white border border-[#22493c]'
              }`}
            >
              {st.label}
            </button>
          ))}
        </div>
      </div>

      {/* Vouchers Table */}
      <div className="rounded-3xl border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] bg-[#152a23] dark:bg-[#152a23] light:bg-white overflow-hidden shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] bg-[#10231d]/70 text-[#90cbb7] font-bold text-[11px] uppercase tracking-wider">
                <th className="py-3.5 px-6">Coupon Code</th>
                <th className="py-3.5 px-6">Benefit</th>
                <th className="py-3.5 px-6">Min Order</th>
                <th className="py-3.5 px-6">Per-User Limit</th>
                <th className="py-3.5 px-6">Redemptions</th>
                <th className="py-3.5 px-6">Schedule / Duration</th>
                <th className="py-3.5 px-6">Status</th>
                <th className="py-3.5 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#22493c]/50">
              {filteredVouchers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-[#90cbb7]">
                    <Ticket className="w-10 h-10 stroke-1 mx-auto mb-2 opacity-40" />
                    <p className="font-bold text-sm text-white">No vouchers found</p>
                    <p className="text-xs text-[#90cbb7]/70 mt-1">
                      Create or schedule an escrow fee coupon code to incentivize new marketplace buyers.
                    </p>
                  </td>
                </tr>
              ) : (
                filteredVouchers.map((v) => {
                  const percentUsed = v.maxUsageCount > 0 ? Math.min(100, Math.round((v.usedCount / v.maxUsageCount) * 100)) : 0;
                  const vStatus = getVoucherStatus(v);

                  return (
                    <tr key={v.code} className="hover:bg-[#1c382f]/30 transition-colors">
                      {/* Code */}
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <code className="font-mono font-bold text-sm text-brand-400 bg-[#10231d] px-2.5 py-1 rounded-xl border border-[#22493c] tracking-wider">
                            {v.code}
                          </code>
                          <button
                            onClick={() => handleCopyCode(v.code)}
                            title="Copy code"
                            className="p-1.5 rounded-lg text-[#90cbb7] hover:text-white hover:bg-[#10231d] transition-colors"
                          >
                            {copiedCode === v.code ? <Check className="w-3.5 h-3.5 text-brand-400" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        <p className="text-[11px] text-[#90cbb7]/70 mt-1 line-clamp-1 max-w-xs">{v.description}</p>
                      </td>

                      {/* Benefit */}
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-extrabold text-white">
                            {v.discountType === 'fixed' ? `₦${v.discountAmount.toLocaleString()}` : `${v.discountAmount}%`}
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-brand-500/20 text-brand-400 border border-brand-500/30">
                            Escrow Credit
                          </span>
                        </div>
                      </td>

                      {/* Min Order */}
                      <td className="py-4 px-6 text-[#90cbb7]">
                        {v.minOrderAmount > 0 ? `₦${v.minOrderAmount.toLocaleString()}` : 'None (₦0)'}
                      </td>

                      {/* Per-User Limit */}
                      <td className="py-4 px-6">
                        {v.usagePerUser === 'once' ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-500/15 text-purple-300 border border-purple-500/30">
                            <Users className="w-3 h-3" />
                            <span>1x per User</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-sky-500/15 text-sky-300 border border-sky-500/30">
                            <RefreshCw className="w-3 h-3" />
                            <span>{v.maxPerUser && v.maxPerUser > 0 ? `Max ${v.maxPerUser}x` : 'Multiple Uses'}</span>
                          </span>
                        )}
                      </td>

                      {/* Redemptions */}
                      <td className="py-4 px-6">
                        <div className="w-32 space-y-1">
                          <div className="flex justify-between text-[10px] font-semibold text-[#90cbb7]">
                            <span>{v.usedCount} used</span>
                            <span>{v.maxUsageCount > 0 ? `${v.maxUsageCount} limit` : 'Unlimited'}</span>
                          </div>
                          {v.maxUsageCount > 0 && (
                            <div className="w-full bg-[#10231d] rounded-full h-1.5 overflow-hidden">
                              <div
                                className="bg-brand-500 h-full rounded-full transition-all"
                                style={{ width: `${percentUsed}%` }}
                              />
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Schedule / Duration */}
                      <td className="py-4 px-6 text-[#90cbb7]">
                        <div className="space-y-0.5 text-[11px]">
                          <div className="flex items-center gap-1.5 text-white/90">
                            <Calendar className="w-3 h-3 text-[#90cbb7]/70" />
                            <span>{v.startsAt ? `From ${v.startsAt}` : 'Immediate start'}</span>
                          </div>
                          <div className="text-[10px] text-[#90cbb7]/60 pl-4.5">
                            {v.expiresAt ? `Until ${v.expiresAt}` : 'No expiration date'}
                          </div>
                        </div>
                      </td>

                      {/* Operational Status */}
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border ${vStatus.bg} ${vStatus.color} ${vStatus.border}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                          <span>{vStatus.label}</span>
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenDetails(v)}
                            title="View Details & Usage Info"
                            className="p-1.5 rounded-xl text-[#90cbb7] hover:text-brand-400 hover:bg-[#10231d] transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenEdit(v)}
                            title="Edit Coupon Details"
                            className="p-1.5 rounded-xl text-[#90cbb7] hover:text-sky-400 hover:bg-[#10231d] transition-colors"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(v)}
                            title={v.isActive ? 'Disable Voucher' : 'Enable Voucher'}
                            className={`p-1.5 rounded-xl transition-colors ${
                              v.isActive 
                                ? 'text-[#90cbb7] hover:text-amber-400 hover:bg-[#10231d]' 
                                : 'text-slate-500 hover:text-emerald-400 hover:bg-[#10231d]'
                            }`}
                          >
                            <Sliders className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(v.code)}
                            title="Delete voucher"
                            className="p-1.5 rounded-xl text-[#90cbb7] hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATE VOUCHER MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-xl bg-[#10231d] border border-[#22493c] rounded-3xl p-6 sm:p-8 text-white shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-[#22493c] pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-brand-500/20 text-brand-400 border border-brand-500/30 flex items-center justify-center">
                  <Ticket className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base sm:text-lg">Create & Schedule Escrow Voucher</h3>
                  <p className="text-xs text-[#90cbb7]">In-App coupon valid for buyer checkout escrow fee discounts</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 rounded-xl text-[#90cbb7] hover:text-white hover:bg-[#152a23]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateVoucher} className="space-y-4 text-xs">
              {/* Code */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-bold text-[#90cbb7]">Coupon / Promo Code</label>
                  <button
                    type="button"
                    onClick={handleGenerateCode}
                    className="text-brand-400 hover:underline font-bold text-[11px] flex items-center gap-1"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Auto-Generate</span>
                  </button>
                </div>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. WELCOME-REUSEIT-2026"
                  required
                  className="w-full px-4 py-3 rounded-2xl font-mono text-sm font-bold tracking-wider bg-[#152a23] border border-[#22493c] text-brand-400 outline-none focus:border-brand-500"
                />
              </div>

              {/* Discount Type & Amount */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-[#90cbb7] mb-1.5">Benefit Type</label>
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value as any)}
                    className="w-full px-3.5 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500 font-semibold"
                  >
                    <option value="fixed">Fixed Naira Discount (₦)</option>
                    <option value="percentage">Percentage Off Fee (%)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-[#90cbb7] mb-1.5">
                    {discountType === 'fixed' ? 'Discount Amount (₦)' : 'Discount Percentage (%)'}
                  </label>
                  <input
                    type="number"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(Number(e.target.value))}
                    min={1}
                    max={discountType === 'percentage' ? 100 : 50000}
                    required
                    className="w-full px-4 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500 font-bold"
                  />
                </div>
              </div>

              {/* Min Order & Total Redemption Limit */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-[#90cbb7] mb-1.5">Min Order Item Price (₦)</label>
                  <input
                    type="number"
                    value={minOrderAmount}
                    onChange={(e) => setMinOrderAmount(Number(e.target.value))}
                    min={0}
                    placeholder="0 for any order"
                    className="w-full px-4 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-[#90cbb7] mb-1.5">Total Campaign Redemption Limit</label>
                  <input
                    type="number"
                    value={maxUsageCount}
                    onChange={(e) => setMaxUsageCount(Number(e.target.value))}
                    min={0}
                    placeholder="0 for unlimited"
                    className="w-full px-4 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500"
                  />
                </div>
              </div>

              {/* Per-User Usage Policy */}
              <div className="p-3.5 rounded-2xl bg-[#152a23]/60 border border-[#22493c] space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-brand-400" />
                  <label className="font-bold text-white">Usage Policy Per Customer</label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label
                    onClick={() => setUsagePerUser('once')}
                    className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      usagePerUser === 'once'
                        ? 'bg-brand-500/15 border-brand-500/40 text-white'
                        : 'bg-[#10231d] border-[#22493c] text-[#90cbb7] hover:border-[#2f6352]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="usagePolicy"
                      checked={usagePerUser === 'once'}
                      onChange={() => setUsagePerUser('once')}
                      className="accent-emerald-400"
                    />
                    <div>
                      <p className="font-bold text-xs text-white">1 Time Per User</p>
                      <p className="text-[10px] text-[#90cbb7]/70">Strictly single checkout</p>
                    </div>
                  </label>

                  <label
                    onClick={() => setUsagePerUser('multiple')}
                    className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      usagePerUser === 'multiple'
                        ? 'bg-brand-500/15 border-brand-500/40 text-white'
                        : 'bg-[#10231d] border-[#22493c] text-[#90cbb7] hover:border-[#2f6352]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="usagePolicy"
                      checked={usagePerUser === 'multiple'}
                      onChange={() => setUsagePerUser('multiple')}
                      className="accent-emerald-400"
                    />
                    <div>
                      <p className="font-bold text-xs text-white">Multiple Times</p>
                      <p className="text-[10px] text-[#90cbb7]/70">Reusable by same buyer</p>
                    </div>
                  </label>
                </div>

                {usagePerUser === 'multiple' && (
                  <div className="pt-2 border-t border-[#22493c]/40 flex items-center justify-between gap-4">
                    <span className="text-[11px] text-[#90cbb7]">Max Redemptions Per Customer:</span>
                    <input
                      type="number"
                      value={maxPerUser}
                      onChange={(e) => setMaxPerUser(Number(e.target.value))}
                      min={0}
                      placeholder="0 for unlimited"
                      className="w-32 px-3 py-1.5 rounded-xl bg-[#10231d] border border-[#22493c] text-white text-xs font-bold outline-none focus:border-brand-500"
                    />
                  </div>
                )}
              </div>

              {/* Active Duration Scheduling */}
              <div className="p-3.5 rounded-2xl bg-[#152a23]/60 border border-[#22493c] space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-sky-400" />
                  <label className="font-bold text-white">Active Duration & Campaign Schedule</label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-[#90cbb7] mb-1">Start Date (Live from)</label>
                    <input
                      type="date"
                      value={startsAt}
                      onChange={(e) => setStartsAt(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-[#10231d] border border-[#22493c] text-white outline-none focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[#90cbb7] mb-1">Expiry Date (Ends on)</label>
                    <input
                      type="date"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-[#10231d] border border-[#22493c] text-white outline-none focus:border-brand-500"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block font-bold text-[#90cbb7] mb-1.5">Voucher Description / User Benefit</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Welcome bonus: ₦1,000 off escrow protection fees for new buyers"
                  className="w-full px-4 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500"
                />
              </div>

              {/* Live Preview Card */}
              <div className="p-4 rounded-2xl bg-gradient-to-r from-[#152a23] to-[#10231d] border border-brand-500/40 relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-brand-400">
                    Live Voucher Preview
                  </span>
                  <span className="text-[10px] font-bold text-white bg-brand-500/20 px-2 py-0.5 rounded-full border border-brand-500/30">
                    Escrow Protection Fee Credit
                  </span>
                </div>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-xl font-black text-white">
                    {discountType === 'fixed' ? `₦${discountAmount || 0}` : `${discountAmount || 0}%`} OFF
                  </span>
                  <span className="text-xs text-[#90cbb7]">Escrow Fee</span>
                </div>
                <div className="mt-2 font-mono font-bold text-brand-300 text-xs bg-[#08130f] px-3 py-1.5 rounded-xl border border-[#22493c] inline-block tracking-wider">
                  {code || 'COUPON-CODE'}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#22493c]">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2.5 rounded-2xl text-xs font-bold text-[#90cbb7] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !code.trim()}
                  className="px-6 py-2.5 rounded-2xl bg-brand-500 hover:bg-brand-400 disabled:opacity-40 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all"
                >
                  {saving ? 'Creating Voucher...' : 'Publish & Schedule Voucher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW DETAILS & USAGE MODAL */}
      {selectedVoucherForDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-[#10231d] border border-[#22493c] rounded-3xl p-6 sm:p-8 text-white shadow-2xl space-y-6">
            <div className="flex items-start justify-between border-b border-[#22493c] pb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-brand-500/20 text-brand-400 border border-brand-500/30 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-extrabold text-lg tracking-wide">{selectedVoucherForDetails.code}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${getVoucherStatus(selectedVoucherForDetails).bg} ${getVoucherStatus(selectedVoucherForDetails).color} ${getVoucherStatus(selectedVoucherForDetails).border}`}>
                      {getVoucherStatus(selectedVoucherForDetails).label}
                    </span>
                  </div>
                  <p className="text-xs text-[#90cbb7] mt-0.5">{selectedVoucherForDetails.description}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedVoucherForDetails(null)}
                className="p-2 rounded-xl text-[#90cbb7] hover:text-white hover:bg-[#152a23]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Quick Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3.5 rounded-2xl bg-[#152a23] border border-[#22493c]">
                <p className="text-[10px] font-bold uppercase text-[#90cbb7] tracking-wider">Times Used</p>
                <p className="text-lg font-black text-white mt-1">
                  {selectedVoucherForDetails.usedCount}
                  {selectedVoucherForDetails.maxUsageCount > 0 ? (
                    <span className="text-xs text-[#90cbb7]/70 font-normal"> / {selectedVoucherForDetails.maxUsageCount}</span>
                  ) : (
                    <span className="text-xs text-[#90cbb7]/70 font-normal"> / ∞</span>
                  )}
                </p>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#152a23] border border-[#22493c]">
                <p className="text-[10px] font-bold uppercase text-[#90cbb7] tracking-wider">Escrow Subsidy</p>
                <p className="text-lg font-black text-emerald-400 mt-1">
                  ₦{(
                    selectedVoucherForDetails.discountType === 'fixed'
                      ? selectedVoucherForDetails.usedCount * selectedVoucherForDetails.discountAmount
                      : selectedVoucherForDetails.usedCount * 1200
                  ).toLocaleString()}
                </p>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#152a23] border border-[#22493c]">
                <p className="text-[10px] font-bold uppercase text-[#90cbb7] tracking-wider">Per-User Policy</p>
                <p className="text-xs font-bold text-purple-300 mt-1.5 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {selectedVoucherForDetails.usagePerUser === 'once' ? '1x per customer' : `Multi (${selectedVoucherForDetails.maxPerUser || '∞'})`}
                </p>
              </div>

              <div className="p-3.5 rounded-2xl bg-[#152a23] border border-[#22493c]">
                <p className="text-[10px] font-bold uppercase text-[#90cbb7] tracking-wider">Schedule</p>
                <p className="text-xs font-bold text-sky-300 mt-1.5 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {selectedVoucherForDetails.expiresAt || 'Ongoing'}
                </p>
              </div>
            </div>

            {/* Campaign Specifications */}
            <div className="p-4 rounded-2xl bg-[#152a23]/60 border border-[#22493c] space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-[#22493c]/50">
                <span className="text-[#90cbb7]">Escrow Benefit:</span>
                <span className="font-bold text-white">
                  {selectedVoucherForDetails.discountType === 'fixed'
                    ? `₦${selectedVoucherForDetails.discountAmount.toLocaleString()} Escrow Credit`
                    : `${selectedVoucherForDetails.discountAmount}% Off Escrow Protection Fee`}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-[#22493c]/50">
                <span className="text-[#90cbb7]">Minimum Qualifying Order:</span>
                <span className="font-bold text-white">
                  {selectedVoucherForDetails.minOrderAmount > 0
                    ? `₦${selectedVoucherForDetails.minOrderAmount.toLocaleString()}`
                    : 'None (₦0)'}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-[#22493c]/50">
                <span className="text-[#90cbb7]">Scheduled Live Window:</span>
                <span className="font-bold text-white">
                  {selectedVoucherForDetails.startsAt ? selectedVoucherForDetails.startsAt : 'Immediate'} ➔{' '}
                  {selectedVoucherForDetails.expiresAt ? selectedVoucherForDetails.expiresAt : 'No expiry'}
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-[#90cbb7]">Created By:</span>
                <span className="font-bold text-[#90cbb7]">{selectedVoucherForDetails.createdBy || 'Super Admin'}</span>
              </div>
            </div>

            {/* Redemptions Log */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-xs text-white uppercase tracking-wider flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-brand-400" />
                  <span>Customer Redemptions Log ({redemptions.length})</span>
                </h4>
                {loadingRedemptions && <RefreshCw className="w-3.5 h-3.5 animate-spin text-[#90cbb7]" />}
              </div>

              <div className="rounded-2xl border border-[#22493c] bg-[#152a23] overflow-hidden max-h-56 overflow-y-auto">
                {redemptions.length === 0 ? (
                  <div className="p-8 text-center text-[#90cbb7]">
                    <Ticket className="w-8 h-8 opacity-30 mx-auto mb-2" />
                    <p className="font-bold text-xs text-white">No customer redemptions recorded yet</p>
                    <p className="text-[11px] text-[#90cbb7]/70 mt-0.5">
                      When buyers enter this code at mobile checkout, their redemptions and saved escrow fee amounts will be recorded here.
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-[#22493c] bg-[#10231d] text-[#90cbb7] text-[10px] uppercase font-bold">
                        <th className="py-2.5 px-4">User</th>
                        <th className="py-2.5 px-4">Order / Item</th>
                        <th className="py-2.5 px-4">Credit Credited</th>
                        <th className="py-2.5 px-4 text-right">Redeemed At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#22493c]/50">
                      {redemptions.map((r, idx) => (
                        <tr key={r.id || idx} className="hover:bg-[#1c382f]/30">
                          <td className="py-2.5 px-4 font-mono text-[11px] text-brand-300">
                            {r.userEmail || r.userId.slice(0, 10) + '...'}
                          </td>
                          <td className="py-2.5 px-4 text-white font-medium text-[11px] truncate max-w-xs">
                            {r.orderTitle || 'Marketplace Item'}
                          </td>
                          <td className="py-2.5 px-4 font-bold text-emerald-400">
                            -₦{r.discountApplied.toLocaleString()}
                          </td>
                          <td className="py-2.5 px-4 text-right text-[11px] text-[#90cbb7]/70">
                            {r.redeemedAt?.toDate ? r.redeemedAt.toDate().toLocaleDateString() : 'Recent'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-[#22493c]">
              <button
                onClick={() => {
                  const toEdit = selectedVoucherForDetails;
                  setSelectedVoucherForDetails(null);
                  handleOpenEdit(toEdit);
                }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-2xl bg-[#152a23] hover:bg-[#1c382f] text-sky-300 border border-sky-500/30 text-xs font-bold transition-all"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Edit Coupon Details</span>
              </button>

              <button
                onClick={() => setSelectedVoucherForDetails(null)}
                className="px-6 py-2 rounded-2xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-xs transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT EXISTING VOUCHER MODAL */}
      {editingVoucher && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-xl bg-[#10231d] border border-[#22493c] rounded-3xl p-6 sm:p-8 text-white shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-[#22493c] pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-sky-500/20 text-sky-400 border border-sky-500/30 flex items-center justify-center">
                  <Edit3 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base sm:text-lg">Edit Coupon: {editingVoucher.code}</h3>
                  <p className="text-xs text-[#90cbb7]">Modify active duration, usage policy, discount benefits, or pause</p>
                </div>
              </div>
              <button
                onClick={() => setEditingVoucher(null)}
                className="p-2 rounded-xl text-[#90cbb7] hover:text-white hover:bg-[#152a23]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4 text-xs">
              {/* Discount Type & Amount */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-[#90cbb7] mb-1.5">Benefit Type</label>
                  <select
                    value={editDiscountType}
                    onChange={(e) => setEditDiscountType(e.target.value as any)}
                    className="w-full px-3.5 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500 font-semibold"
                  >
                    <option value="fixed">Fixed Naira Discount (₦)</option>
                    <option value="percentage">Percentage Off Fee (%)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-[#90cbb7] mb-1.5">
                    {editDiscountType === 'fixed' ? 'Discount Amount (₦)' : 'Discount Percentage (%)'}
                  </label>
                  <input
                    type="number"
                    value={editDiscountAmount}
                    onChange={(e) => setEditDiscountAmount(Number(e.target.value))}
                    min={1}
                    max={editDiscountType === 'percentage' ? 100 : 50000}
                    required
                    className="w-full px-4 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500 font-bold"
                  />
                </div>
              </div>

              {/* Min Order & Total Redemption Limit */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-[#90cbb7] mb-1.5">Min Order Item Price (₦)</label>
                  <input
                    type="number"
                    value={editMinOrderAmount}
                    onChange={(e) => setEditMinOrderAmount(Number(e.target.value))}
                    min={0}
                    className="w-full px-4 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-[#90cbb7] mb-1.5">Total Campaign Redemption Limit</label>
                  <input
                    type="number"
                    value={editMaxUsageCount}
                    onChange={(e) => setEditMaxUsageCount(Number(e.target.value))}
                    min={0}
                    placeholder="0 for unlimited"
                    className="w-full px-4 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500"
                  />
                </div>
              </div>

              {/* Per-User Usage Policy */}
              <div className="p-3.5 rounded-2xl bg-[#152a23]/60 border border-[#22493c] space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-brand-400" />
                  <label className="font-bold text-white">Usage Policy Per Customer</label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label
                    onClick={() => setEditUsagePerUser('once')}
                    className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      editUsagePerUser === 'once'
                        ? 'bg-brand-500/15 border-brand-500/40 text-white'
                        : 'bg-[#10231d] border-[#22493c] text-[#90cbb7] hover:border-[#2f6352]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="editUsagePolicy"
                      checked={editUsagePerUser === 'once'}
                      onChange={() => setEditUsagePerUser('once')}
                      className="accent-emerald-400"
                    />
                    <div>
                      <p className="font-bold text-xs text-white">1 Time Per User</p>
                      <p className="text-[10px] text-[#90cbb7]/70">Strictly single checkout</p>
                    </div>
                  </label>

                  <label
                    onClick={() => setEditUsagePerUser('multiple')}
                    className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      editUsagePerUser === 'multiple'
                        ? 'bg-brand-500/15 border-brand-500/40 text-white'
                        : 'bg-[#10231d] border-[#22493c] text-[#90cbb7] hover:border-[#2f6352]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="editUsagePolicy"
                      checked={editUsagePerUser === 'multiple'}
                      onChange={() => setEditUsagePerUser('multiple')}
                      className="accent-emerald-400"
                    />
                    <div>
                      <p className="font-bold text-xs text-white">Multiple Times</p>
                      <p className="text-[10px] text-[#90cbb7]/70">Reusable by same buyer</p>
                    </div>
                  </label>
                </div>

                {editUsagePerUser === 'multiple' && (
                  <div className="pt-2 border-t border-[#22493c]/40 flex items-center justify-between gap-4">
                    <span className="text-[11px] text-[#90cbb7]">Max Redemptions Per Customer:</span>
                    <input
                      type="number"
                      value={editMaxPerUser}
                      onChange={(e) => setEditMaxPerUser(Number(e.target.value))}
                      min={0}
                      placeholder="0 for unlimited"
                      className="w-32 px-3 py-1.5 rounded-xl bg-[#10231d] border border-[#22493c] text-white text-xs font-bold outline-none focus:border-brand-500"
                    />
                  </div>
                )}
              </div>

              {/* Active Duration Scheduling */}
              <div className="p-3.5 rounded-2xl bg-[#152a23]/60 border border-[#22493c] space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-sky-400" />
                  <label className="font-bold text-white">Active Duration & Campaign Schedule</label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-[#90cbb7] mb-1">Start Date (Live from)</label>
                    <input
                      type="date"
                      value={editStartsAt}
                      onChange={(e) => setEditStartsAt(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-[#10231d] border border-[#22493c] text-white outline-none focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[#90cbb7] mb-1">Expiry Date (Ends on)</label>
                    <input
                      type="date"
                      value={editExpiresAt}
                      onChange={(e) => setEditExpiresAt(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-[#10231d] border border-[#22493c] text-white outline-none focus:border-brand-500"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block font-bold text-[#90cbb7] mb-1.5">Voucher Description / User Benefit</label>
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl bg-[#152a23] border border-[#22493c] text-white outline-none focus:border-brand-500"
                />
              </div>

              {/* Active Toggle Switch */}
              <div className="p-3.5 rounded-2xl bg-[#152a23]/60 border border-[#22493c] flex items-center justify-between">
                <div>
                  <p className="font-bold text-xs text-white">Coupon Status</p>
                  <p className="text-[11px] text-[#90cbb7]/70">Turn off to pause or disable this coupon code immediately</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditIsActive(!editIsActive)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 border ${
                    editIsActive
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                      : 'bg-slate-500/20 text-slate-400 border-slate-500/40'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${editIsActive ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`} />
                  <span>{editIsActive ? 'Active' : 'Disabled'}</span>
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#22493c]">
                <button
                  type="button"
                  onClick={() => setEditingVoucher(null)}
                  className="px-4 py-2.5 rounded-2xl text-xs font-bold text-[#90cbb7] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="px-6 py-2.5 rounded-2xl bg-brand-500 hover:bg-brand-400 disabled:opacity-40 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all"
                >
                  {savingEdit ? 'Saving Changes...' : 'Save & Update Voucher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
